
import { type FC, useCallback, useState } from 'react';
// import {
//   useNavigate,
// } from 'react-router-dom';
import {
  UiButton,
  UiHStack,
  UiModal,
  UiModalBody,
  UiModalContent,
  UiModalOverlay,
  UiStack,
  UiText,
  uiStyles, UiTag, UiSpinner,
} from '@/lib/ui';
import { registration } from '@/api';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import { useLocale } from '@/registration/component/Locale/LocaleProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiErrorHandler } from '@/account/hook/useApiErrorHandler';
import { useTenantApi } from '@/account/hook/useTenantApi';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { generatePageUrl } from '@/app/pages';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { groupRegistrationQueryKey } from '@/api/registration/groupRegistration';

export interface LocaleSwitchConfirmationProps {
  tenantCode: string,
  eventId: string,
  toLocale: registration.LocaleData,
  isOpen: boolean,
  onClose: () => void,
  isGroup?: boolean,
}

const LocaleSwitchConfirmation: FC<LocaleSwitchConfirmationProps> = ({
  tenantCode,
  eventId,
  toLocale,
  isOpen,
  onClose,
  isGroup = false,
}) => {
  const queryClient = useQueryClient();
  const {currentLocale, changeLocale} = useLocale();
  const [errors, setErrors] = useState<string[]>([]);
  const { reportToGlobal } = useApiErrorHandler();
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const { data: registrationData } = useRegistrationQuery(eventId);

  // Call the backend to change the locale.
  const { mutate, isLoading } = useMutation<registration.ChangeLocaleResponse, Error, registration.ChangeLocaleRequest>({
    mutationFn: async (data: registration.ChangeLocaleRequest) => {
      return await registration.changeLocale(createTenantApiRequest)(data);
    },
    onSuccess: (result) => {
      if (result?.errors && Array.isArray(result?.errors) && result?.errors.length > 0) {
        setErrors(result?.errors ?? []);
      } else {
        setErrors([]);
        changeLocale(eventId, toLocale);
        onClose();
        // Always redirect the user to the main tickets page once a locale is changed.
        if (isGroup) {
          window.location.href = generatePageUrl('RegistrationRegisterGroupMainTickets', { tenantCode, eventId });
        } else {
          window.location.href = generatePageUrl('RegistrationRegisterMainTickets', { tenantCode, eventId });
        }
      }
    },
    onError: (error) => {
      reportToGlobal(error);
      setErrors([error.message ?? 'Failed to change the currency.']);
    }
  });

  const onLocaleChange = useCallback(()=> {
    if (currentLocale?.id !== toLocale.id) {
      mutate({
        eventId,
        localeId: toLocale?.id ?? 0,
        userId: registrationData?.userId ?? 0,
        isGroup,
      });
    }
  }, [
    eventId,
    toLocale,
    currentLocale,
    onClose,
  ]);

  return (
    <UiModal
      onClose={onClose}
      size={'md'}
      isOpen={isOpen}
      motionPreset={'scale'}
      isCentered={true}
    >
      <UiModalOverlay />
      <UiModalContent
        borderRadius={uiStyles.borderRadius}
      >
        <UiModalBody>
          {isApiPreparing ? (
            <UiHStack p={8}>
              <UiSpinner size={'md'} color={'primary.500'} thickness='2px' />
            </UiHStack>
          ) : (
            <UiStack
              // px={2}
              py={4}
              flexGrow={1}
              alignItems={'stretch'}
              // enableDivider={true}
              spacing={4}
              // maxW={clientWidth}
            >
              {errors && errors.length > 0 && (
                <UiStack flexGrow={1}>
                  {errors.map((error) => {
                    return (
                      <BaseMessageBarError key={error} mt={3}>{error}</BaseMessageBarError>
                    );
                  })}
                </UiStack>
              )}
              <UiText>
                You are switching the currency to <UiTag>{toLocale.currency}</UiTag>. This will
              </UiText>
              <BaseDividerHorizontal height={0} />
              <UiStack pt={2}>
                <BaseMessageBarInfo
                  borderRadius={uiStyles.borderRadius}
                  bgColor={'yellow.50'}
                >
                  Reset your registration.
                </BaseMessageBarInfo>
                <BaseMessageBarInfo
                  borderRadius={uiStyles.borderRadius}
                  bgColor={'yellow.50'}
                >
                  Empty your shopping cart.
                </BaseMessageBarInfo>
              </UiStack>
              {/*<UiStack p={4} bgColor={'blackAlpha.50'} borderRadius={uiStyles.borderRadius}>*/}
              {/*  <UiText>Reset your registration.</UiText>*/}
              {/*  <UiText>Empty your shopping cart.</UiText>*/}
              {/*</UiStack>*/}
              <UiText>
                Do you wish to continue?
              </UiText>
              <UiHStack flexGrow={1} justifyContent={'space-between'}>
                <UiButton variant={'ghost'} color={'red.500'} pl={0} py={0} onClick={onClose}>No</UiButton>
                <UiButton onClick={onLocaleChange} isLoading={isLoading}>Yes</UiButton>
              </UiHStack>
            </UiStack>
          )}
        </UiModalBody>
      </UiModalContent>
    </UiModal>
  );
};

export default LocaleSwitchConfirmation;
